import { Spin } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { Tab, Nav } from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import { pifGet, pifPost, pifPostRaw } from "../../../helpers/api_helper";
import {
  GET_PIF_IntegrationStatus,
  GET_PIF_AccountingConfig,
  POST_PIF_AccountingConfig,
  GET_PIF_UomList,
  POST_PIF_UomSave,
  GET_PIF_MasterMirror,
  POST_PIF_GeneratePairingCode,
} from "../../../helpers/url_helper";
import { PIF_INTEGRATION_STATUS, pifDateTime } from "./pifConstants";
import { successMessage, errorMessage, customConfirmDialog } from "../CommonFunctions";

const fmt = pifDateTime;

// the ledger-mapping form fields — key -> label
const LEDGER_FIELDS = [
  ["VoucherTypeName", "Sales voucher type"],
  ["SalesLedgerName", "Sales ledger"],
  ["CgstLedgerName", "Output CGST ledger"],
  ["SgstLedgerName", "Output SGST ledger"],
  ["IgstLedgerName", "Output IGST ledger"],
  ["RoundOffLedgerName", "Round-off ledger"],
  ["PartyParentGroup", "Customer (party) group"],
  ["LedgerParentGroupSales", "Sales-ledger parent group"],
  ["StockItemParentGroup", "Test-item group"],
  ["DefaultUnitName", "Default unit (unit-less lines)"],
];

const symbolOk = (s) => s && /^[^\s-]+$/.test(s.trim());

// "YYYY-MM-DD" from the picker's local date parts (no UTC shift) for the date-only CutOffDate.
const ymdLocal = (d) => {
  if (!d) return null;
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const IntegrationConfig = () => {
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const companyIdEnc = AuthUser?.CompanyIDEncrypt;
  const formsList = AuthUser?.Permissions?.FormList;
  const canEdit =
    formsList?.filter((x) => x?.FormID == 111 && x?.IsEdit)?.length > 0;

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [units, setUnits] = useState([]);
  const [mirrorNames, setMirrorNames] = useState([]);
  const [newCode, setNewCode] = useState(null); // freshly generated pairing code (shown prominently)

  const enc = encodeURIComponent(companyIdEnc);

  const load = async () => {
    setIsLoading(true);
    try {
      const [st, c, u, m] = await Promise.all([
        pifGet(`${GET_PIF_IntegrationStatus}?companyIdEnc=${enc}`),
        pifGet(`${GET_PIF_AccountingConfig}?companyIdEnc=${enc}`),
        pifGet(`${GET_PIF_UomList}?companyIdEnc=${enc}`),
        pifGet(`${GET_PIF_MasterMirror}?companyIdEnc=${enc}`),
      ]);
      setStatus(st || null);
      setCfg(c || {});
      setUnits((u || []).map((r) => ({ ...r })));
      setMirrorNames([...new Set((m || []).map((x) => x.TallyName).filter(Boolean))]);
    } catch {
      errorMessage("Could not load the integration configuration.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveLedgers = async () => {
    setIsLoading(true);
    try {
      const res = await pifPost(POST_PIF_AccountingConfig, {
        ...cfg,
        ServiceItemMode: cfg?.ServiceItemMode ?? 1, // schema default; the form doesn't expose it
        CompanyIdEnc: companyIdEnc,
      });
      res?.IsSuccess
        ? successMessage(res?.Message || "Saved.")
        : errorMessage(res?.Message || "Save failed.");
    } catch {
      errorMessage("Could not save the ledger mapping.");
    }
    setIsLoading(false);
    load();
  };

  const saveUnits = async () => {
    const bad = units.filter((u) => u.TallyUnitName && !symbolOk(u.TallyUnitName));
    if (bad.length) {
      errorMessage("Tally unit symbols cannot contain spaces or hyphens.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await pifPostRaw(
        `${POST_PIF_UomSave}?companyIdEnc=${enc}`,
        units.map((u) => ({
          LimsUnitId: u.LimsUnitId,
          LimsUnitName: u.LimsUnitName,
          TallyUnitName: (u.TallyUnitName || "").trim(),
        }))
      );
      res?.IsSuccess
        ? successMessage("Unit mapping saved.")
        : errorMessage(res?.Message || "Save failed.");
    } catch {
      errorMessage("Could not save the unit mapping.");
    }
    setIsLoading(false);
    load();
  };

  const generateCode = () => {
    customConfirmDialog(
      "Generate pairing code",
      "Issues a new one-time code for pairing a Tally agent with this company. Any existing unused code is replaced. Valid for 7 days.",
      async () => {
        setIsLoading(true);
        try {
          const res = await pifPost(POST_PIF_GeneratePairingCode, {
            CompanyIdEnc: companyIdEnc,
          });
          if (res?.IsSuccess && res?.PairingCode) {
            setNewCode(res.PairingCode);
            successMessage("Pairing code generated.");
            load();
          } else {
            errorMessage(res?.Message || "Could not generate a pairing code.");
          }
        } catch {
          errorMessage("Could not generate a pairing code.");
        }
        setIsLoading(false);
      },
      true
    );
  };

  const si = PIF_INTEGRATION_STATUS[status?.Status] || {
    label: "Not configured",
    badge: "secondary",
  };
  const hbAge = status?.LastHeartbeatAgeMinutes ?? null; // server-computed, not the browser clock
  const agentUp = hbAge != null && hbAge <= 10;

  const checklist = useMemo(
    () =>
      status
        ? [
            ["Tally company bound", !!status.TallyCompanyName],
            ["Agent connected", agentUp],
            ["Ledger mapping saved", !!status.HasConfig],
            ["Company GST plan set", !!status.HasGstPlan],
            ["All units mapped", status.UnmappedUnitCount === 0],
          ]
        : [],
    [status, agentUp]
  );

  return (
    <div className="card m-1">
      <div className="card-body p-2">
        <Spin size="large" spinning={isLoading} tip="Loading..." style={{ position: "fixed" }}>
          <div className="tbl-caption p-1 d-flex justify-content-between align-items-center">
            <h4 className="heading mb-0">Tally Integration — Configuration</h4>
            <button className="btn btn-outline-primary btn-sm" onClick={load}>
              <i className="fa fa-refresh me-1" /> Refresh
            </button>
          </div>
          <hr className="mt-0 mb-2" />

          <div className="default-tab">
            <Tab.Container defaultActiveKey="connection">
              <Nav as="ul" className="nav-tabs">
                <Nav.Item as="li"><Nav.Link eventKey="connection"><i className="la la-plug me-2" />Connection</Nav.Link></Nav.Item>
                <Nav.Item as="li"><Nav.Link eventKey="ledgers"><i className="la la-book me-2" />Ledger Mapping</Nav.Link></Nav.Item>
                <Nav.Item as="li"><Nav.Link eventKey="units"><i className="la la-balance-scale me-2" />Unit Mapping</Nav.Link></Nav.Item>
              </Nav>

              <Tab.Content className="pt-3">
                {/* ---- Connection (S5) ---- */}
                <Tab.Pane eventKey="connection">
                  {canEdit && (
                    <div className="card border mb-3">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                          <div>
                            <h6 className="mb-1">Agent pairing</h6>
                            <p className="text-muted fs-13 mb-0">
                              The agent installer asks for a pairing code. Generate one here, then run{" "}
                              <code>install-agent.ps1 … -PairingCode &lt;code&gt;</code> on the Tally PC.
                            </p>
                          </div>
                          <button className="btn btn-primary btn-sm" onClick={generateCode}>
                            <i className="fa fa-key me-1" /> Generate pairing code
                          </button>
                        </div>
                        {(newCode || status?.PairingCode) && (
                          <div className="alert alert-success mt-3 mb-0 py-2">
                            <div className="d-flex align-items-center">
                              <span className="me-2">Pairing code:</span>
                              <code className="fw-bold" style={{ fontSize: "1.1rem" }}>{newCode || status?.PairingCode}</code>
                            </div>
                            <div className="text-muted fs-12 mt-1">
                              {newCode
                                ? "Valid for 7 days and usable once — it is consumed when the agent pairs."
                                : "This code has not been used yet."}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!status ? (
                    <div className="alert alert-info py-2">
                      No agent has paired with this company yet. Generate a pairing code above,
                      install the agent on the Tally PC with it, then set the ledger mapping.
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-lg-6">
                        <table className="table table-sm">
                          <tbody>
                            <tr><td className="text-muted">Status</td><td><span className={`badge badge-${si.badge} light`}>{si.label}</span></td></tr>
                            <tr><td className="text-muted">Tally company</td><td>{status.TallyCompanyName || "—"}</td></tr>
                            <tr><td className="text-muted">Tally GUID</td><td><code>{status.TallyCompanyGuid || "—"}</code></td></tr>
                            <tr><td className="text-muted">Agent</td><td>{status.AgentVersion || "—"} @ {status.MachineName || "—"}</td></tr>
                            <tr><td className="text-muted">Last heartbeat</td><td>{fmt(status.LastHeartbeatUtc)}{hbAge != null ? ` (${hbAge} min ago)` : ""}</td></tr>
                            <tr><td className="text-muted">Last validation</td><td>{fmt(status.LastValidationOnUtc)}</td></tr>
                            <tr><td className="text-muted">Pairing code</td><td>{status.PairingCode ? <code>{status.PairingCode}</code> : <span className="text-muted">consumed</span>}</td></tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="col-lg-6">
                        <h6>Readiness</h6>
                        <ul className="list-unstyled">
                          {checklist.map(([label, ok]) => (
                            <li key={label} className="mb-1">
                              <i className={`fa ${ok ? "fa-check-circle text-success" : "fa-times-circle text-danger"} me-2`} />
                              {label}
                              {label === "All units mapped" && !ok && status.UnmappedUnitCount > 0 && (
                                <span className="text-muted"> ({status.UnmappedUnitCount} left)</span>
                              )}
                            </li>
                          ))}
                        </ul>
                        <h6 className="mt-3">Companies open in Tally</h6>
                        {status.LoadedCompanies?.length ? (
                          <ul className="mb-0">
                            {status.LoadedCompanies.map((c) => (
                              <li key={c.Guid || c.Name}>
                                {c.Name}
                                {c.Guid && c.Guid.toLowerCase() === status.TallyCompanyGuid?.toLowerCase() && (
                                  <span className="badge badge-success light ms-2">bound</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-muted">none reported (agent offline?)</span>
                        )}
                      </div>
                    </div>
                  )}
                </Tab.Pane>

                {/* ---- Ledger Mapping (S6) ---- */}
                <Tab.Pane eventKey="ledgers">
                  {(!cfg?.SalesLedgerName || !cfg?.VoucherTypeName) && (
                    <div className="alert alert-warning py-2">
                      Every field below must match a name that already exists in the customer's Tally.
                    </div>
                  )}
                  <datalist id="pif-tally-names">
                    {mirrorNames.map((n) => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                  <div className="row">
                    {LEDGER_FIELDS.map(([key, label]) => (
                      <div className="col-lg-6 mb-2" key={key}>
                        <label className="mb-1">{label}</label>
                        <input
                          className="form-control form-control-sm"
                          list="pif-tally-names"
                          value={cfg?.[key] || ""}
                          disabled={!canEdit}
                          onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
                        />
                      </div>
                    ))}
                    <div className="col-lg-6 mb-2">
                      <label className="mb-1">
                        Cut-off date
                        <span className="text-muted fs-12 ms-1">
                          — only invoices on/after this date sync; blank = all invoices
                        </span>
                      </label>
                      <Flatpickr
                        className="form-control form-control-sm"
                        options={{ altInput: true, altFormat: "j-M-Y", dateFormat: "d-m-Y" }}
                        value={cfg?.CutOffDate ? new Date(cfg.CutOffDate) : undefined}
                        disabled={!canEdit}
                        onChange={(dates) =>
                          setCfg({ ...cfg, CutOffDate: ymdLocal(dates?.[0]) })
                        }
                      />
                    </div>
                  </div>
                  {canEdit && (
                    <div className="stickyBottomButtons">
                      <hr className="mt-1 mb-2" />
                      <button className="btn btn-primary btn-sm" onClick={saveLedgers}>
                        <i className="fa-solid fa-floppy-disk me-1" /> Save ledger mapping
                      </button>
                    </div>
                  )}
                </Tab.Pane>

                {/* ---- Unit Mapping (S7) ---- */}
                <Tab.Pane eventKey="units">
                  <p className="text-muted">
                    Approve the Tally unit symbol for each LIMS unit — no spaces or hyphens.
                    Unmapped units block any invoice that uses them.
                  </p>
                  <div className="table-responsive active-projects style-1 shorting">
                    <table className="table dataTable no-footer mb-0">
                      <thead>
                        <tr>
                          <th>LIMS unit</th>
                          <th>Tally symbol</th>
                          <th>State</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {units.length === 0 ? (
                          <tr><td colSpan={4}>No units.</td></tr>
                        ) : (
                          units.map((u, i) => {
                            const filled = !!(u.TallyUnitName || "").trim();
                            const ok = filled && symbolOk(u.TallyUnitName);
                            return (
                              <tr key={u.LimsUnitId}>
                                <td>{u.LimsUnitName}</td>
                                <td>
                                  <input
                                    className={`form-control form-control-sm ${filled && !ok ? "is-invalid" : ""}`}
                                    style={{ maxWidth: 280 }}
                                    value={u.TallyUnitName || ""}
                                    disabled={!canEdit}
                                    onChange={(e) => {
                                      const next = [...units];
                                      next[i] = { ...u, TallyUnitName: e.target.value };
                                      setUnits(next);
                                    }}
                                  />
                                </td>
                                <td>
                                  {!filled ? (
                                    <span className="badge badge-danger light">unmapped</span>
                                  ) : ok ? (
                                    <span className="badge badge-success light">ok</span>
                                  ) : (
                                    <span className="badge badge-warning light">invalid</span>
                                  )}
                                </td>
                                <td />
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  {canEdit && (
                    <div className="stickyBottomButtons">
                      <hr className="mt-1 mb-2" />
                      <button className="btn btn-primary btn-sm" onClick={saveUnits}>
                        <i className="fa-solid fa-floppy-disk me-1" /> Save unit mapping
                      </button>
                    </div>
                  )}
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default IntegrationConfig;
